import { useState, useCallback, useRef } from "react";
import { ArrowUpDown, ChevronUp, ChevronDown, Building2, Loader2 } from "lucide-react";
import { ALL_COLUMNS, DEFAULT_COL_WIDTHS, COL_WIDTHS_KEY, loadColWidths, getLetzteAktivitaet } from "./helpers";
import { InlineKontaktart } from "./InlineKontaktart";
import { InlineVerantwortlicher } from "./InlineVerantwortlicher";
import { RowContextMenu } from "./RowContextMenu";

export function CustomerTable({ contacts, selectedId, onSelect, onDblClick, sortField, sortDir, onSort, loading, onRefreshList, users, visibleCols }) {
  const columns = visibleCols.map((key) => ALL_COLUMNS.find((c) => c.key === key)).filter(Boolean);

  const [colWidths, setColWidths] = useState(loadColWidths);
  const resizingRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState(null);

  const onMouseDown = useCallback((e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[key] || DEFAULT_COL_WIDTHS[key] || 100;
    resizingRef.current = { key, startX, startW };

    const onMouseMove = (ev) => {
      const ref = resizingRef.current;
      if (!ref) return;
      const diff = ev.clientX - ref.startX;
      const newW = Math.max(40, ref.startW + diff);
      setColWidths((prev) => ({ ...prev, [key]: newW }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setColWidths((prev) => {
        localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(prev));
        return prev;
      });
      resizingRef.current = null;
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [colWidths]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-gray-300" />;
    return sortDir === "asc"
      ? <ChevronUp className="w-3 h-3 text-gray-700" />
      : <ChevronDown className="w-3 h-3 text-gray-700" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Laden...
      </div>
    );
  }

  return (
    <div className="overflow-auto" data-testid="customer-table-wrapper">
      <table className="w-full caption-bottom text-[11px]" style={{ tableLayout: "fixed" }}>
        <colgroup>
          {columns.map((col) => (
            <col key={col.key} style={{ width: colWidths[col.key] }} />
          ))}
        </colgroup>
        <thead className="[&_tr]:border-b">
          <tr className="bg-gray-50 hover:bg-gray-50 border-b transition-colors">
            {columns.map((col) => (
              <th
                key={col.key}
                className="h-6 px-2 text-left align-middle font-medium text-muted-foreground text-[10px] font-semibold text-gray-500 uppercase tracking-wider select-none whitespace-nowrap py-1 relative"
                data-testid={`sort-${col.key}`}
              >
                <span
                  className="flex items-center gap-1 cursor-pointer"
                  onClick={() => onSort(col.key)}
                >
                  {col.label} <SortIcon field={col.key} />
                </span>
                <div
                  className="absolute top-0 right-0 w-[5px] h-full cursor-col-resize hover:bg-blue-400/40 active:bg-blue-500/50 z-10"
                  onMouseDown={(e) => onMouseDown(e, col.key)}
                  data-testid={`resize-${col.key}`}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&_tr:last-child]:border-0">
          {contacts.length === 0 ? (
            <tr className="border-b">
              <td colSpan={columns.length} className="text-center py-8 text-gray-400 text-[11px]">
                <Building2 className="w-7 h-7 mx-auto mb-1.5 text-gray-300" />
                Keine Kunden gefunden
              </td>
            </tr>
          ) : (
            contacts.map((c) => {
              const la = getLetzteAktivitaet(c.letzte_aktivitaet);
              const isSelected = selectedId === c.id;
              const cellCls = "py-1 px-2 whitespace-nowrap overflow-hidden text-ellipsis text-[11px]";
              const cellRenderers = {
                kundennummer: () => <td key="kundennummer" className={`${cellCls} font-medium text-gray-900`}>{c.kundennummer || ""}</td>,
                firma: () => <td key="firma" className={`${cellCls} font-medium text-gray-900`}>{c.firma || ""}</td>,
                kontaktart: () => <td key="kontaktart" className={cellCls}><InlineKontaktart value={c.kontaktart} contactId={c.id} onChanged={onRefreshList} /></td>,
                verantwortlicher: () => <td key="verantwortlicher" className={cellCls}><InlineVerantwortlicher value={c.verantwortlicher} contactId={c.id} users={users} onChanged={onRefreshList} /></td>,
                plz: () => <td key="plz" className={`${cellCls} text-gray-600`}>{c.plz || ""}</td>,
                ort: () => <td key="ort" className={`${cellCls} text-gray-600`}>{c.ort || ""}</td>,
                strasse: () => <td key="strasse" className={`${cellCls} text-gray-600`}>{c.strasse || c.adresse || ""}</td>,
                telefon: () => <td key="telefon" className={`${cellCls} text-gray-600`}>{c.telefon || ""}</td>,
                letzte_aktivitaet: () => (
                  <td key="letzte_aktivitaet" className={`${cellCls} text-gray-500`} data-testid={`activity-cell-${c.id}`}>
                    {la.text ? (
                      <span>
                        <span className="text-gray-700">{la.text}</span>
                        {la.benutzer && <span className="text-gray-400"> – {la.benutzer}</span>}
                        {la.zeit && <span className="text-gray-400"> – {la.zeit}</span>}
                      </span>
                    ) : (
                      <span className="text-gray-400">{la.zeit}</span>
                    )}
                  </td>
                ),
              };
              return (
                <tr
                  key={c.id}
                  onClick={() => onSelect(c.id)}
                  onDoubleClick={(e) => { e.preventDefault(); onDblClick(c.id); }}
                  onContextMenu={(e) => { e.preventDefault(); onSelect(c.id); setCtxMenu({ x: e.clientX, y: e.clientY, contact: c }); }}
                  className={`cursor-pointer border-b transition-colors select-none ${
                    isSelected ? "bg-blue-50 border-l-2 border-l-blue-600" : "hover:bg-gray-50"
                  }`}
                  data-testid={`customer-row-${c.id}`}
                >
                  {columns.map((col) => cellRenderers[col.key]())}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {ctxMenu && (
        <RowContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          contact={ctxMenu.contact}
          onClose={() => setCtxMenu(null)}
          onSelect={onSelect}
          onEdit={onDblClick}
          onDelete={(id) => { if (selectedId === id) onSelect(null); onRefreshList(); }}
          onRefresh={onRefreshList}
        />
      )}
    </div>
  );
}
